import { cn } from "@design-system/utils";
import * as React from "react";

import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "../empty";

interface ErrorProps extends Omit<React.ComponentProps<"div">, "onError"> {
  error?: Error;
  onTrackError?: (error: Error) => void;
}

function Error({
  error,
  onTrackError,
  children,
  className,
  ...props
}: ErrorProps) {
  React.useEffect(() => {
    if (error && onTrackError) {
      onTrackError(error);
    }
  }, [error, onTrackError]);

  return (
    <Empty role="alert" className={className} {...props}>
      {children}
    </Empty>
  );
}

interface ErrorDetailsProps extends React.ComponentProps<"details"> {
  error: Error;
}

function ErrorDetails({ error, className, ...props }: ErrorDetailsProps) {
  return (
    <details
      data-slot="error-details"
      className={cn("w-full max-w-md", className)}
      {...props}
    >
      <summary className="text-foreground-neutral-muted cursor-pointer text-sm">
        Technical details
      </summary>
      <pre className="bg-shape-neutral-filled-background mt-2 overflow-auto rounded-md p-4 text-left text-xs">
        {error.stack || error.message}
      </pre>
    </details>
  );
}

Error.Media = EmptyMedia;
Error.Header = EmptyHeader;
Error.Title = EmptyTitle;
Error.Description = EmptyDescription;
Error.Actions = EmptyContent;
Error.Details = ErrorDetails;

export { Error, ErrorDetails };
export type { ErrorProps, ErrorDetailsProps };
